import { Litepicker } from "litepicker"
import { toDateInput } from "./working_days"

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

export interface DueDatePicker {
  isOpen(): boolean
  hide(): void
}

// A `YYYY-MM-DD` value as a local date, or null.
export function parseDateInput(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)

  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null
}

// Litepicker compares its bounds against each day at 00:00, so a bound carrying the clock time
// would lock out today itself.
export function startOfToday(): Date {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return today
}

// "Mon 29 Sep", with the year when it isn't this year.
export function dueDateLabel(value: string): string {
  const date = parseDateInput(value)
  if (!date) return "Pick a date"

  const label = `${DAYS[date.getDay()]} ${date.getDate()} ${MONTHS[date.getMonth()]}`

  return date.getFullYear() === new Date().getFullYear() ? label : `${label} ${date.getFullYear()}`
}

// The Todo page's Reschedule calendar (todo.ts openPicker): rebuilt on every open and destroyed
// once a day is picked or it hides. Every day before today is disabled; there is no upper bound.
export function openDueDatePicker(input: HTMLInputElement, anchor: HTMLElement, onPick: (date: string) => void): DueDatePicker {
  const today = startOfToday()
  const kept = input.value
  const current = parseDateInput(kept)
  const overdue = !current || current < today

  // Litepicker reads the input's own value, so an overdue date would open on its month, marked as
  // chosen. It is held back here and put back if nothing is picked.
  if (overdue) input.value = ""

  const picker = new Litepicker({
    element: input,
    singleMode: true,
    autoApply: true,
    format: "YYYY-MM-DD",
    minDate: today,
    startDate: (!overdue && current) || undefined
  })

  // Destroyed pickers must not be touched again, hence the flag.
  let open = true
  let picked = false
  const destroy = (): void => {
    if (!picked) input.value = kept
    open = false
    picker.destroy()
  }

  picker.on("selected", (date: any) => {
    picked = true
    onPick(toDateInput(date.dateInstance ? date.dateInstance : date))
    destroy()
  })
  picker.on("hide", destroy)
  picker.show(anchor)

  return {
    isOpen: () => open,
    hide: () => { if (open) picker.hide() }
  }
}
